import React, { useState, useRef, useEffect } from 'react';
import ScannedItem from '../ScannerItem/ScannerItem';
import { CartItem, Book } from '../../types/book.types';
import { QrCode, RotateCcw, ArrowBigRight, FileText, MapPin, AlertTriangle } from 'lucide-react';
import './LeftColCart.css';
import { formatCurrency } from '../../utils/currency';
import { CartTotals } from '../../hooks/useCartTotals';
import { TopScannerIndicator } from '../TopScannerIndicator/TopScannerIndicator';
import { ClientSelectionModal } from '../ClientSelectionModal/ClientSelectionModal';
import { ShippingModal } from '../ShippingModal/ShippingModal';
import { DerModal } from '../DerModal/DerModal';
import { DerTraveler } from '../../types/der.types';
import { EmailPromptModal } from '../EmailPromptModal/EmailPromptModal';
import { DIAS_DEVOLUCION, diasDesdeLaCompra, fueraDePlazo } from '../../constants/returns.constants';
import { SaleReturnViewType } from '../../types/view.types';
import { BillingSelection, Client } from '../../types/client.types';
import { PromoCodes } from '../PromoCodes/PromoCodes';
import { PromoCodeType } from '../../types/promo.types';
import { PROMO_CODES, CODE_PROMO } from '../../mocks/PromoCodes.mocks';
import { RightColNumpad, RightColNumpadProps } from '../RightColNumpad/RightColNumpad';
import { PaymentModal, PaymentMethodType } from '../PaymentModal/PaymentModal';
import { CardScanModal, CardScanConfig, MARCIAL_CARD, GIFT_CARD } from '../CardScanModal/CardScanModal';
import { PromoCodeModal } from '../PromoCodeModal/PromoCodeModal';
import { DiscountModal } from '../DiscountModal/DiscountModal';
import { RecommendationsModal } from '../RecommendationsModal/RecommendationsModal';
import { getRecommendations, BookRecommendation } from '../../services/BooksService';
import { useClients } from '../../hooks/useClients';

export interface LeftColCartProps {
    cart: CartItem[];
    view: SaleReturnViewType;
    /** Id de la factura cargada para devolver; null si no hay ninguna. */
    currentReturnTxId?: string | null;
    /** Fecha del ticket cargado, para avisar si cae fuera del plazo de devolucion. */
    currentReturnDate?: Date | null;
    /** Descarga la factura cargada ("Cambiar Ticket"). */
    onChangeTicket?: () => void;
    /** Devuelve la factura entera, con las cantidades originales. */
    onFullReturn?: () => void;
    updateQuantity: (id: string, delta: number) => void;
    /** Fija el descuento directo (%) de una linea del carrito. */
    onApplyDiscount: (id: string, percentage: number) => void;
    /** Añade al ticket un libro sugerido (misma regla que escanearlo: stock incluido). */
    onAddRecommendation: (book: Book) => void;
    simulateRandomScan: () => void;
    totals: CartTotals;
    scannerStatus: string;
    currentCashier: any;
    selectedPromo: PromoCodeType | null;
    setSelectedPromo: (promo: PromoCodeType | null) => void;
    isNumpadHidden: boolean;
    onShowNumpad: () => void;
    onBackgroundClick: () => void;
    handleCobrarClick: () => void;
    onProcessPayment?: (
        method: PaymentMethodType,
        clientInfo?: {
            client?: any;
            authorizedPerson?: any;
            generateInvoice?: boolean;
            isTaxFree?: boolean;
            /** Direccion a la que mandar la factura emitida; null = no mandarla. */
            invoiceEmail?: string | null;
            /** Viajero para solicitar el DER; null = no se pide. */
            der?: DerTraveler | null;
        },
        /** Codigo leido de la tarjeta, cuando el metodo pasa por CardScanModal. */
        cardCode?: string
    ) => void;
    enableCashChangeCalculator?: boolean;
    billing: BillingSelection;
    setBilling: React.Dispatch<React.SetStateAction<BillingSelection>>;
    numpadProps: RightColNumpadProps;
    showToast?: (title: string, description: string, iconType?: 'success' | 'alert') => void;
}

export const LeftColCart: React.FC<LeftColCartProps> = ({
    cart,
    view,
    currentReturnTxId,
    currentReturnDate,
    onChangeTicket,
    onFullReturn,
    updateQuantity,
    onApplyDiscount,
    onAddRecommendation,
    simulateRandomScan,
    totals,
    scannerStatus,
    currentCashier,
    selectedPromo,
    setSelectedPromo,
    isNumpadHidden,
    onShowNumpad,
    onBackgroundClick,
    handleCobrarClick,
    onProcessPayment,
    enableCashChangeCalculator = true,
    billing,
    setBilling,
    numpadProps,
    showToast
}) => {
    /** Los datos de facturacion son un solo objeto: aqui se toca campo a campo. */
    const patchBilling = (patch: Partial<BillingSelection>) =>
        setBilling(prev => ({ ...prev, ...patch }));

    const [showClientModal, setShowClientModal] = useState(false);
    const [showShippingModal, setShowShippingModal] = useState(false);
    const [showInvoiceEmailModal, setShowInvoiceEmailModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    /** Tarjeta cuyo codigo se esta pidiendo; null = no hay modal de tarjeta. */
    const [cardScan, setCardScan] = useState<{ method: PaymentMethodType; config: CardScanConfig } | null>(null);
    /** Linea del carrito a la que se esta configurando el descuento directo. */
    const [discountTarget, setDiscountTarget] = useState<CartItem | null>(null);

    /** Las tarjetas con codigo pasan antes por CardScanModal a capturarlo. */
    const CARD_SCANS: Partial<Record<PaymentMethodType, CardScanConfig>> = {
        marcial_card: MARCIAL_CARD,
        gift_card: GIFT_CARD
    };

    /** Lanza el cobro con los datos de facturacion ya elegidos. */
    /**
     * La venta lleva factura solo si se ha pedido: el interruptor, un envio, tax
     * free, un DER, pagar contra factura o tener cliente. Si no, es un ticket y nada mas.
     */
    const invoiceWanted = (method?: PaymentMethodType): boolean =>
        billing.generateInvoice || billing.isTaxFree || !!billing.shippingAddress || !!billing.client
        || (derRequested && !!billing.client) || method === 'invoice';

    const launchPayment = (method: PaymentMethodType, cardCode?: string, emailOverride?: string | null) => {
        if (!onProcessPayment) return;
        onProcessPayment(
            method,
            {
                client: billing.client,
                authorizedPerson: billing.authorizedPerson,
                generateInvoice: invoiceWanted(method),
                isTaxFree: billing.isTaxFree,
                der: billing.client && derRequested ? der : null,
                // Sin envio no hay direccion que mandar: el cobro no tiene que
                // volver a decidirlo.
                invoiceEmail: emailOverride !== undefined
                    ? emailOverride
                    : sendEmail && invoiceWanted(method) ? billing.invoiceEmail || null : null
            },
            cardCode
        );
        // El DER y el envio son de este cobro: el siguiente arranca limpio.
        setDer(null);
        setDerRequested(false);
    };

    /** Viajero del DER pedido para este cobro; null = no se pide. */
    const [der, setDer] = useState<DerTraveler | null>(null);
    const [showDerModal, setShowDerModal] = useState(false);
    /** Casilla del DER marcada. Puede estarlo sin datos todavia: entonces no se puede cobrar. */
    const [derRequested, setDerRequested] = useState(false);

    /** Selector de la ventana de pago: por defecto SI se envia, y se apaga caso a caso. */
    const [sendEmail, setSendEmail] = useState(true);

    /**
     * Cobro pendiente de saber a que email va la factura. Solo se pregunta
     * cuando el envio esta activado y no hay de donde sacar la direccion: sin
     * cliente, o con un cliente sin email en su ficha.
     */
    const [pendingPay, setPendingPay] = useState<{ method: PaymentMethodType; cardCode?: string } | null>(null);

    const startPayment = (method: PaymentMethodType, cardCode?: string) => {
        // La direccion es la de la ficha del cliente o la que se tecleo con el
        // sobre (billing.invoiceEmail); si no hay ninguna, se pide ahora.
        const needsEmail = invoiceWanted(method) && sendEmail && !billing.invoiceEmail;
        setSendEmail(true);
        if (needsEmail) setPendingPay({ method, cardCode });
        else launchPayment(method, cardCode);
    };

    // Sugerencias de venta de una linea: mismo autor o misma categoria.
    const [recsTarget, setRecsTarget] = useState<CartItem | null>(null);
    const [recs, setRecs] = useState<BookRecommendation[]>([]);
    const [isLoadingRecs, setIsLoadingRecs] = useState(false);
    const [recsError, setRecsError] = useState<string | null>(null);
    /** Evita que la respuesta de una linea pise a la de otra abierta despues. */
    const recsRequestId = useRef(0);

    const openRecommendations = (item: CartItem) => {
        const requestId = ++recsRequestId.current;
        setRecsTarget(item);
        setRecs([]);
        setRecsError(null);
        setIsLoadingRecs(true);

        // Lo que ya esta en el ticket no se vuelve a sugerir.
        getRecommendations(item, cart.map(i => i.id))
            .then(found => {
                if (requestId !== recsRequestId.current) return;
                setRecs(found);
                setIsLoadingRecs(false);
            })
            .catch(err => {
                if (requestId !== recsRequestId.current) return;
                setRecsError(err?.message || 'No se pudieron cargar las sugerencias.');
                setIsLoadingRecs(false);
            });
    };

    const closeRecommendations = () => {
        recsRequestId.current++;
        setRecsTarget(null);
    };
    /** Metodo de pago en curso cuando el cobro se interrumpe por falta de cliente. */
    const [pendingPaymentMethod, setPendingPaymentMethod] = useState<PaymentMethodType | null>(null);

    // Promos
    const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);

    // Accordion State for Totals Bar
    const totalsBarRef = useRef<HTMLDivElement>(null);
    const [totalsWidth, setTotalsWidth] = useState(1200);
    const [activeSection, setActiveSection] = useState<'promos' | 'info' | 'buy' | null>(null);

    useEffect(() => {
        const ro = new (window as any).ResizeObserver((entries: any) => {
            for (let entry of entries) {
                setTotalsWidth(entry.contentRect.width);
            }
        });
        if (totalsBarRef.current) ro.observe(totalsBarRef.current);
        return () => ro.disconnect();
    }, []);

    let isPromosExpanded = true;
    let isInfoExpanded = true;
    let isBuyExpanded = true;

    // Totals bar responsive breakpoints
    // Width < 800: Stage 2 (Only 1 expanded, default Buy)
    // Width < 1050: Stage 1 (2 expanded, default Info + Buy, Promos collapsed)
    if (totalsWidth < 800) {
        isPromosExpanded = activeSection === 'promos';
        isInfoExpanded = activeSection === 'info';
        isBuyExpanded = activeSection === 'buy' || (!activeSection && true);
    } else if (totalsWidth < 1050) {
        if (activeSection === 'promos') {
            isPromosExpanded = true;
            isInfoExpanded = false;
            isBuyExpanded = true;
        } else if (activeSection === 'info') {
            isPromosExpanded = false;
            isInfoExpanded = true;
            isBuyExpanded = true;
        } else {
            isPromosExpanded = false;
            isInfoExpanded = true;
            isBuyExpanded = true;
        }
    } else {
        isPromosExpanded = true;
        isInfoExpanded = true;
        isBuyExpanded = true;
    }

    const handleSectionClick = (section: 'promos' | 'info' | 'buy') => {
        setActiveSection(section);
    };

    // Clients state
    const {
        clients,
        setClients,
        isLoading: isLoadingClients,
        error: clientsError
    } = useClients(showClientModal);

    /**
     * Si el selector se abrio porque faltaba cliente al cobrar, hay que volver
     * al pago con el metodo que ya estaba elegido, no dejar al cajero tirado en
     * la pantalla de venta teniendo que empezar el cobro otra vez.
     */
    const closeClientModal = (resumePayment: boolean) => {
        setShowClientModal(false);
        if (resumePayment && pendingPaymentMethod) {
            setShowPaymentModal(true);
        } else {
            setPendingPaymentMethod(null);
        }
    };

    const handleSelectClient = (client: Client, person: any) => {
        // La direccion de envio y el email de la factura son del cliente
        // anterior: al cambiar de cliente se descartan, o se acabaria enviando
        // a la direccion de otro. El email arranca con el de la ficha del nuevo,
        // listo para usarse de un clic, pero sin dar el envio por hecho.
        patchBilling({
            client,
            authorizedPerson: person,
            shippingAddress: null,
            invoiceEmail: client.email || '',
            sendInvoiceEmail: false
        });
        // Con el selector del DER marcado, los datos del viajero se piden ya.
        if (derRequested) setShowDerModal(true);
        closeClientModal(true);
        showToast?.('Cliente Seleccionado', `Se ha seleccionado a ${client.companyName}.`, 'success');
    };

    const handleAddClient = async (client: Client) => {
        // Optimistic update handled by the modal, or we just do it here
        setClients([...clients, client]);
        patchBilling({ client, shippingAddress: null, invoiceEmail: client.email || '', sendInvoiceEmail: false });
        if (derRequested) setShowDerModal(true);
        closeClientModal(true);
        showToast?.('Cliente Seleccionado', `Se ha seleccionado a ${client.companyName}.`, 'success');
    };

    return (
        <div
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onBackgroundClick();
            }}
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minWidth: 0 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <TopScannerIndicator
                        scannerStatus={scannerStatus}
                        selectedClient={billing.client}
                        selectedAuthorizedPerson={billing.authorizedPerson}
                        selectedShippingAddress={billing.shippingAddress}
                        generateInvoice={billing.generateInvoice}
                        isTaxFree={billing.isTaxFree}
                        invoiceEmail={billing.invoiceEmail}
                        sendInvoiceEmail={billing.sendInvoiceEmail}
                        currentCashier={currentCashier}
                        setShowClientModal={setShowClientModal}
                        setShowShippingModal={setShowShippingModal}
                        setGenerateInvoice={(v) => patchBilling({ generateInvoice: v })}
                        setIsTaxFree={(v) => patchBilling({ isTaxFree: v })}
                        setSendInvoiceEmail={(v) => patchBilling({ sendInvoiceEmail: v })}
                        onEditInvoiceEmail={() => setShowInvoiceEmailModal(true)}
                        setSelectedClient={(c) => patchBilling({ client: c })}
                        setSelectedAuthorizedPerson={(p) => patchBilling({ authorizedPerson: p })}
                        setSelectedShippingAddress={(a) => patchBilling({ shippingAddress: a })}
                        isNumpadHidden={isNumpadHidden}
                        onShowNumpad={onShowNumpad}
                    />

                    {/* Lista del carrito */}
                    <div
                        className="al-cart-list al-scrollbar-hide"
                        onClick={(e) => { if (e.target === e.currentTarget) onBackgroundClick(); }}
                    >
                        <div
                            className="al-cart-inner"
                            onClick={(e) => { if (e.target === e.currentTarget) onBackgroundClick(); }}
                        >
                            {view === 'returns' && currentReturnTxId && (
                                <div className="al-return-ticket-banner">
                                    <div className="al-return-ticket-banner-left">
                                        <FileText style={{ width: '1.25rem', height: '1.25rem' }} />
                                        <span>Factura {currentReturnTxId} cargada</span>
                                        {/* Avisa, no impide: devolver fuera de plazo lo decide el cajero. */}
                                        {currentReturnDate && fueraDePlazo(currentReturnDate) && (
                                            <span className="al-return-ticket-banner-warning">
                                                <AlertTriangle style={{ width: '1rem', height: '1rem' }} />
                                                {`Fuera de plazo: ${diasDesdeLaCompra(currentReturnDate)} días desde la compra (plazo ${DIAS_DEVOLUCION})`}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={onChangeTicket}
                                        className="al-return-ticket-banner-btn"
                                    >
                                        Cambiar Ticket
                                    </button>
                                </div>
                            )}
                            {cart.length > 0 ? (
                                <>
                                    {/*
                                      * El envio no es un articulo del carrito: es una linea
                                      * calculada que se pinta encima de los libros. En
                                      * devoluciones no sale, que no se devuelve el porte.
                                      */}
                                    {view !== 'returns' && billing.shippingAddress && (
                                        <div className="al-shipping-line">
                                            <div className="al-shipping-left">
                                                <div className="al-shipping-icon">
                                                    <MapPin style={{ width: '1.5rem', height: '1.5rem' }} />
                                                </div>
                                                <div>
                                                    <div className="al-shipping-tags">
                                                        <span className="al-shipping-tag">Servicio de Envío</span>
                                                        <span className="al-shipping-badge">AUTOMÁTICO</span>
                                                    </div>
                                                    <h4 className="al-shipping-title">Gastos de Envío y Manipulación</h4>
                                                    <p className="al-shipping-detail">
                                                        {totals.totalWeight.toFixed(2)} kg acumulados
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {cart.map(item => (
                                        <ScannedItem
                                            key={item.id}
                                            item={item}
                                            onQtyChange={(d) => updateQuantity(item.id, d)}
                                            onDiscount={() => setDiscountTarget(item)}
                                            onRecommendations={() => openRecommendations(item)}
                                            isReturnMode={view === 'returns'}
                                        />
                                    ))}
                                </>
                            ) : (
                                <div className="al-cart-empty" onClick={onBackgroundClick}>
                                    <QrCode style={{ width: '6rem', height: '6rem', marginBottom: '1.5rem' }} />
                                    <p>{view === 'returns' ? 'Escanea Ticket o busca por ID' : 'Escanea un libro o introduce ISBN/SKU'}</p>
                                    <p>{view === 'returns' ? 'Introduce ID de transacción' : 'Teclado táctil para búsqueda manual'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <RightColNumpad {...numpadProps} />
            </div>

            {/* Barra de totales */}
            <div className="al-totals-bar" ref={totalsBarRef}>
                <div className="al-totals-left" style={{ gap: isInfoExpanded ? '2.5rem' : '1.5rem', transition: 'gap 0.3s ease' }}>

                    {/* Section 2: Info (Articulos, Descuento, IVA) */}
                    <div
                        className={`al-section-info ${!isInfoExpanded ? 'al-section-collapsed' : ''}`}
                        onClickCapture={(e) => {
                            if (!isInfoExpanded) {
                                e.stopPropagation();
                                e.preventDefault();
                                handleSectionClick('info');
                            }
                        }}
                        style={{
                            display: 'flex',
                            gap: isInfoExpanded ? '2.5rem' : '1rem',
                            cursor: !isInfoExpanded ? 'pointer' : 'default',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isInfoExpanded && (
                            <div className="al-total-block">
                                <p className="al-total-label al-total-label--gray">Artículos</p>
                                <p className="al-total-value">{cart.reduce((a, b) => a + b.quantity, 0)}</p>
                            </div>
                        )}
                        <div className="al-total-block">
                            <p className="al-total-label al-total-label--red">Descuento</p>
                            <p className={`al-total-value${(totals.discountsTotal > 0 || totals.promoDiscount > 0) ? ' al-total-value--red' : ' al-total-value--muted'}`}>
                                {formatCurrency(totals.discountsTotal + totals.promoDiscount)}
                            </p>
                        </div>
                        {isInfoExpanded && (
                            <div className="al-vat-block">
                                <p className="al-total-label al-total-label--gray">IVA (4%)</p>
                                <p className="al-total-value">{formatCurrency(totals.total * 0.04)}</p>
                            </div>
                        )}
                    </div>

                    {/* Section 1: Promociones */}
                    <div
                        className={`al-section-promos ${!isPromosExpanded ? 'al-section-collapsed' : ''}`}
                        onClickCapture={(e) => {
                            if (!isPromosExpanded) {
                                e.stopPropagation();
                                e.preventDefault();
                                handleSectionClick('promos');
                            }
                        }}
                        style={{
                            flex: isPromosExpanded ? 1 : '0 0 170px',
                            minWidth: 0,
                            cursor: !isPromosExpanded ? 'pointer' : 'default',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            position: 'relative',
                            zIndex: 10
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 0, pointerEvents: !isPromosExpanded ? 'none' : 'auto' }}>
                            <PromoCodes
                                view={view}
                                selectedPromo={selectedPromo}
                                setSelectedPromo={setSelectedPromo}
                                setShowPromoCodeModal={setShowPromoCodeModal}
                                promoCodes={PROMO_CODES}
                            />
                        </div>
                    </div>
                </div>

                {/* Section 3: Buy Button Section */}
                <div
                    className={`al-section-buy ${!isBuyExpanded ? 'al-section-collapsed' : ''}`}
                    onClickCapture={(e) => {
                        if (!isBuyExpanded) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSectionClick('buy');
                        }
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: !isBuyExpanded ? 'pointer' : 'default',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <div className="al-grand-total">
                        <p className="al-grand-total-label">{view === 'returns' ? 'Total Devolver' : 'Total a Pagar'}</p>
                        <p className="al-grand-total-value">
                            {formatCurrency(totals.total)}
                        </p>
                    </div>
                    {/*
                      Fuera de plazo el boton se apaga aunque el ticket este
                      cargado: puede venir de Movimientos, que no pasa por el
                      buscador de devoluciones.
                    */}
                    {isBuyExpanded && (
                        <button
                            onClick={() => {
                                // En devoluciones no hay cobro que abrir: se
                                // devuelve la factura entera, con las cantidades
                                // originales y no las marcadas en el carrito.
                                if (view === 'returns') {
                                    onFullReturn?.();
                                    return;
                                }
                                // Una linea de PEDIDO (sin stock, con proveedor) genera un
                                // pedido para el cliente: sin cliente no se abre el cobro.
                                if (cart.some(i => i.selectedSource) && !billing.client) {
                                    showToast?.(
                                        'Cliente Requerido',
                                        'Venta con artículos sin stock (genera pedido). Es obligatorio seleccionar un cliente para poder contactarle.',
                                        'alert'
                                    );
                                    setShowClientModal(true);
                                    return;
                                }
                                handleCobrarClick();
                                setShowPaymentModal(true);
                            }}
                            disabled={view === 'returns'
                                ? !currentReturnTxId || (!!currentReturnDate && fueraDePlazo(currentReturnDate))
                                : cart.length === 0}
                            title={view === 'returns' && !!currentReturnDate && fueraDePlazo(currentReturnDate)
                                ? `Fuera del plazo de devolución de ${DIAS_DEVOLUCION} días`
                                : undefined}
                            className={`action-btn-cobrar ${view === 'sale' ? 'action-btn-cobrar-sale' : 'action-btn-cobrar-returns'}`}
                        >
                            <span className="action-btn-cobrar-text">
                                {view === 'returns' ? 'DEVOLUCIÓN TOTAL' : 'COBRAR'}
                            </span>
                            {view === 'returns' ? (
                                <RotateCcw className="lucide lucide-arrow-big-right action-button-icon" />
                            ) : (
                                <ArrowBigRight className="lucide lucide-arrow-big-right action-button-icon" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showClientModal && (
                <ClientSelectionModal
                    onClose={() => closeClientModal(false)}
                    onSelect={handleSelectClient}
                    onAddClient={handleAddClient}
                    clients={clients}
                    selectedClient={billing.client}
                    selectedPerson={billing.authorizedPerson}
                    der={{
                        checked: derRequested,
                        onChange: (on) => { setDerRequested(on); if (!on) setDer(null); }
                    }}
                    isLoading={isLoadingClients}
                    error={clientsError}
                />
            )}

            {showShippingModal && billing.client && (
                <ShippingModal
                    client={billing.client}
                    onClose={() => setShowShippingModal(false)}
                    onConfirm={(address) => {
                        // Solo afecta a lo que se ve: la chapa de la direccion y
                        // la linea de envio del carrito. No llega al total ni a
                        // la factura, porque MainApp pasa null a useCartTotals y
                        // registerSale no sabe nada del envio.
                        patchBilling({ shippingAddress: address, generateInvoice: true });
                        setShowShippingModal(false);
                    }}
                />
            )}

            {showInvoiceEmailModal && billing.client && (
                <EmailPromptModal
                    title="Enviar Factura por Email"
                    clientName={billing.client.companyName}
                    fichaEmail={billing.client.email}
                    initialEmail={billing.invoiceEmail}
                    confirmLabel="ENVIAR AL COBRAR"
                    hint="Se usará solo para esta factura. La ficha del cliente no cambia."
                    onClose={() => setShowInvoiceEmailModal(false)}
                    onConfirm={(email) => {
                        patchBilling({ invoiceEmail: email, sendInvoiceEmail: true });
                        setShowInvoiceEmailModal(false);
                    }}
                />
            )}

            {showDerModal && billing.client && (
                <DerModal
                    clientName={billing.client.companyName}
                    initial={der}
                    onClose={() => setShowDerModal(false)}
                    onConfirm={(traveler) => { setDer(traveler); setShowDerModal(false); }}
                />
            )}

            {pendingPay && (
                <EmailPromptModal
                    title="Enviar Factura por Email"
                    clientName={billing.client?.companyName || 'Cliente varios'}
                    confirmLabel="COBRAR Y ENVIAR"
                    skipLabel="COBRAR SIN ENVIAR"
                    hint="Se usará solo para esta factura. La ficha del cliente no cambia."
                    onClose={() => setPendingPay(null)}
                    onSkip={() => {
                        const { method, cardCode } = pendingPay;
                        setPendingPay(null);
                        launchPayment(method, cardCode, null);
                    }}
                    onConfirm={(email) => {
                        const { method, cardCode } = pendingPay;
                        setPendingPay(null);
                        launchPayment(method, cardCode, email);
                    }}
                />
            )}

            {recsTarget && (
                <RecommendationsModal
                    target={recsTarget}
                    recommendations={recs}
                    isLoading={isLoadingRecs}
                    error={recsError}
                    onClose={closeRecommendations}
                    onAdd={(book) => {
                        onAddRecommendation(book);
                        closeRecommendations();
                    }}
                />
            )}

            {discountTarget && (
                <DiscountModal
                    item={discountTarget}
                    onClose={() => setDiscountTarget(null)}
                    onConfirm={(percentage) => {
                        onApplyDiscount(discountTarget.id, percentage);
                        setDiscountTarget(null);
                    }}
                />
            )}

            {showPaymentModal && (
                <PaymentModal
                    total={totals.total}
                    isClientSelected={!!billing.client}
                    selectedPromo={selectedPromo}
                    onSelectPromo={(promo) => {
                        // "Codigo Descuento" no se aplica a dedo: hay que
                        // canjear un codigo, tambien desde la pantalla de cobro.
                        if (promo && promo.code === CODE_PROMO) {
                            setShowPromoCodeModal(true);
                        } else {
                            setSelectedPromo(promo);
                        }
                    }}
                    view={view}
                    itemsCount={cart.reduce((a, b) => a + b.quantity, 0)}
                    discountsTotal={totals.discountsTotal}
                    promoDiscount={totals.promoDiscount}
                    shippingCost={totals.shippingCost || 0}
                    enableCashChangeCalculator={enableCashChangeCalculator}
                    initialMethod={pendingPaymentMethod}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setPendingPaymentMethod(null);
                    }}
                    sendEmail={sendEmail}
                    onSendEmailChange={invoiceWanted() ? setSendEmail : undefined}
                    emailTarget={billing.invoiceEmail}
                    derRequested={derRequested}
                    derIncomplete={derRequested && !der}
                    onDerEdit={() => setShowDerModal(true)}
                    onDerToggle={(on) => {
                        setDerRequested(on);
                        if (on) setShowDerModal(true);
                        else setDer(null);
                    }}
                    onRequestClientSelection={(method) => {
                        setPendingPaymentMethod(method);
                        setShowPaymentModal(false);
                        setShowClientModal(true);
                        showToast?.('Cliente Requerido', 'Debes seleccionar un cliente para proceder con el pago.', 'alert');
                    }}
                    onConfirm={(method) => {
                        setShowPaymentModal(false);
                        setPendingPaymentMethod(null);
                        // Las tarjetas con codigo pasan antes por su modal a
                        // capturarlo; el resto de metodos cobra directamente.
                        const scan = CARD_SCANS[method];
                        if (scan) {
                            setCardScan({ method, config: scan });
                            return;
                        }
                        startPayment(method);
                    }}
                />
            )}

            {showPromoCodeModal && (
                <PromoCodeModal
                    onClose={() => setShowPromoCodeModal(false)}
                    onConfirm={(promo) => {
                        setSelectedPromo(promo);
                        setShowPromoCodeModal(false);
                    }}
                />
            )}

            {cardScan && (
                <CardScanModal
                    config={cardScan.config}
                    onClose={() => setCardScan(null)}
                    onConfirm={(cardCode) => {
                        const { method } = cardScan;
                        setCardScan(null);
                        startPayment(method, cardCode);
                    }}
                />
            )}

        </div>
    );
};

